/**
 * DirectLine Health Probe Module
 *
 * Provides lightweight health checking for DirectLine connections.
 * Continuously sends minimal health pings until the backend acknowledges
 * one on the same websocket, ensuring the channel is ready for real traffic.
 *
 * NOTE: This module no longer subscribes to activity$ directly. Instead, it
 * relies on the durable subscription in ChatSlice_Store to call notifyHealthAck()
 * when a health_ack control message arrives.
 */

import { DirectLine, Activity } from "botframework-directlinejs";
import {
  CONTROL_EVENT_HEALTH_PROBE,
} from "./constants/chatControlEvents";

// Phase 1: Import identity utilities for health probes
import {
  acquireDelegatedGraphToken,
} from "./utils/msalTokenService";
import { attachIdentityToActivity } from "./directLlineClient";

// Module-level state for health probe tracking
let healthProbeInterval: ReturnType<typeof setInterval> | null = null;
let healthProbeTimeoutId: ReturnType<typeof setTimeout> | null = null;
let healthProbeResolve: (() => void) | null = null;
let healthProbeReject: ((error: Error) => void) | null = null;

// Module-level types removed as they're not used after refactoring

/**
 * Notify that a health_ack has been received from the durable subscription.
 * This is called by ChatSlice_Store when it observes a health_ack control message.
 */
export function notifyHealthAck(): void {
  if (healthProbeResolve) {
    const resolve = healthProbeResolve;
    // Clear reject before stopHealthProbe so it doesn't reject the promise
    // that we're about to resolve (happy path)
    healthProbeReject = null;
    stopHealthProbe();
    resolve();
  }
}

/**
 * Start health probe loop on a DirectLine instance.
 * Continuously sends health probe activities until acknowledgement is received
 * via notifyHealthAck() being called from the durable subscription.
 *
 * @param {DirectLine} directLine - The DirectLine instance to probe
 * @param {number} intervalMs - How often to send probes (default: 1000ms)
 * @param {number} timeoutMs - Maximum time to wait for ack (default: 30000ms)
 * @returns {Promise<void>} Resolves when health ack is received
 */
export function startHealthProbe(
  directLine: DirectLine,
  intervalMs: number = 1000,
  timeoutMs: number = 30000
): Promise<void> {
  // Clean up any existing probe
  stopHealthProbe();

  return new Promise<void>((resolve, reject) => {
    healthProbeResolve = resolve;
    healthProbeReject = reject;

    let probeCount = 0;

    // Set up timeout
    healthProbeTimeoutId = setTimeout(() => {
      const savedReject = healthProbeReject;
      // Clear reject before stopHealthProbe so it doesn't mask the timeout
      // error with an AbortError (same pattern as notifyHealthAck)
      healthProbeReject = null;
      stopHealthProbe();
      if (savedReject) {
        savedReject(
          new Error(
            `DirectLine health probe timeout - no acknowledgement after ${
              timeoutMs / 1000
            } seconds`
          )
        );
      }
    }, timeoutMs);

    // Function to send a single health probe
    const sendProbe = async (): Promise<void> => {
      // Check if probe was already resolved
      if (!healthProbeResolve) {
        return;
      }

      probeCount++;

      // Phase 1: Build activity with identity
      let activity: Activity = {
        from: { id: "user", role: "user" },
        type: "message",
        text: "", // Empty text for control messages
        channelData: {
          message_type: "control",
          control_event: CONTROL_EVENT_HEALTH_PROBE,
        },
      };

      // Phase 1: Attach identity to health probe
      try {
        const delegatedToken = await acquireDelegatedGraphToken();
        activity = attachIdentityToActivity(activity, delegatedToken);
      } catch (tokenError) {
        // Health probes are lightweight, continue without identity if token fails
        console.warn("⚠️ Health probe: Failed to acquire delegated token:", tokenError);
      }

      directLine
        .postActivity(activity)
        .subscribe({
          next: () => {},
          error: () => {}
        });
    };

    // Send first probe immediately
    sendProbe();

    // Set up interval for subsequent probes
    healthProbeInterval = setInterval(sendProbe, intervalMs);
  });
}

/**
 * Stop the health probe loop and clean up timers.
 * Safe to call multiple times.
 */
export function stopHealthProbe(): void {
  if (healthProbeInterval) {
    clearInterval(healthProbeInterval);
    healthProbeInterval = null;
  }

  if (healthProbeTimeoutId) {
    clearTimeout(healthProbeTimeoutId);
    healthProbeTimeoutId = null;
  }

  // Reject pending promise so callers don't hang forever (e.g. when a
  // project switch supersedes the current flow mid-health-probe)
  if (healthProbeReject) {
    const reject = healthProbeReject;
    healthProbeReject = null;
    healthProbeResolve = null;
    reject(new DOMException('Health probe stopped', 'AbortError'));
  } else {
    healthProbeResolve = null;
    healthProbeReject = null;
  }
}

/**
 * Convenience function that waits for DirectLine to be online,
 * then runs the health probe to ensure the channel is fully ready.
 *
 * @param {DirectLine} directLine - The DirectLine instance
 * @param {number} intervalMs - How often to send probes
 * @param {number} timeoutMs - Maximum time to wait for ack
 * @returns {Promise<DirectLine>} Resolves with DirectLine instance when healthy
 */
export async function ensureDirectLineHealthy(
  directLine: DirectLine,
  intervalMs: number = 1000,
  timeoutMs: number = 30000
): Promise<DirectLine> {

  // Wait for health probe to complete
  await startHealthProbe(directLine, intervalMs, timeoutMs);

  return directLine;
}
